import {
  DataFlaggerNodeData,
  DataFlaggerNodeDataCondition,
  PromptColumnsService,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Info, Plus, Trash2 } from 'lucide-react';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';

type ConditionOperator = 'eq' | 'contains'; // We can expand the operators as needed
type Condition = {
  operator: ConditionOperator;
  value: string; // We can change or expand the type based on your use case
  id: string;
  agentId: string;
};
type AgentConditionsConfigurationType = Condition[] | null;

export default function DataFlaggerNodeConfiguration({
  setNodes,
  selectedNode,
  readOnly = false,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
}) {
  const nodes = useNodes();
  const wF = useStore($workflow);
  const queryClient = useQueryClient();
  const org = useStore($org);

  const [agentConditions, setAgentConditions] = useState<
    DataFlaggerNodeDataCondition[]
  >([]);

  const [notifyIfAnyDataIsFlagged, setNotifyIfAnyDataIsFlagged] =
    useState<boolean>(false);
  const [
    flaggedDataNotificationReceipients,
    setFlaggedDataNotificationReceipients,
  ] = useState<string>('');

  // Function to validate the shape of the data
  const isValidAgentConditionsConfiguration = (
    conditions: any,
  ): conditions is AgentConditionsConfigurationType => {
    return conditions && Array.isArray(conditions);
  };

  function updateAgentConditionsOnNode(
    agentConditions = null,
    notifyIfAnyDataIsFlagged: boolean = false,
    flaggedDataNotificationReceipients: string = '',
  ) {
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      agentConditions &&
        (updatableNode.data = {
          ...updatableNode.data,
          conditions: agentConditions,
          type: 'DATA_FLAGGER' as WorkflowNodeType,
        });

      updatableNode.data.notifyIfAnyDataIsFlagged = notifyIfAnyDataIsFlagged;

      if (flaggedDataNotificationReceipients !== null) {
        updatableNode.data.flaggedDataNotificationReceipients =
          flaggedDataNotificationReceipients;
      }

      // set nodes
      newNodes[selectedNodeIndex] = updatableNode;

      setNodes(newNodes);
    }
  }

  function addNewCondition(condition: {
    agentId: string;
    operator: ConditionOperator;
    value: string; // We can change or expand the type based on your use case
  }) {
    const existingConditions = JSON.parse(
      JSON.stringify(agentConditions || []),
    );

    // insert the new condition
    const conditionId = nanoid(12);
    const newConditions = [
      ...existingConditions,
      { ...condition, id: conditionId },
    ];

    setAgentConditions(newConditions);
  }

  function updateAgentCondition(condition: {
    agentId: string;
    operator: string; // We can change or expand the type based on your use case
    value: string;
    id: string;
  }) {
    // get all the existing conditions
    const existingConditions = JSON.parse(
      JSON.stringify(agentConditions || []),
    );

    // updated the condition based on condition id
    const foundConditionToUpdate = existingConditions.find((c: any) => {
      return c.id === condition.id;
    });
    // find condition index
    const conditionIndex = existingConditions.findIndex(
      (c: any) => c.id === condition.id,
    );

    if (foundConditionToUpdate) {
      foundConditionToUpdate.value = condition.value;
      foundConditionToUpdate.operator = condition.operator;
      foundConditionToUpdate.agentId = condition.agentId;
    }

    existingConditions[conditionIndex] = foundConditionToUpdate;

    setAgentConditions(existingConditions);
    if (condition.agentId !== '') {
      updateAgentConditionsOnNode(
        existingConditions,
        notifyIfAnyDataIsFlagged,
        flaggedDataNotificationReceipients,
      );
    }
  }

  function removeAgentCondition(conditionId: string) {
    // get all the existing conditions
    const existingConditions = JSON.parse(
      JSON.stringify(agentConditions || []),
    );

    // remove the condition
    const newConditions = existingConditions.filter(
      (c: any) => c.id !== conditionId,
    );

    setAgentConditions(newConditions);
    updateAgentConditionsOnNode(
      newConditions,
      notifyIfAnyDataIsFlagged,
      flaggedDataNotificationReceipients,
    );
  }

  // Asumes that there is only one analyzer node present in the workflow
  // and anayzer node is compulsory for data flagger node
  // This selected agents will be used in the condition builder
  const [selectedAgents, setSelectedAgents] = useState<any[]>([]);

  const promptColumns = useQuery({
    queryKey: ['promptsColumns', org?.id],
    queryFn: async () => {
      if (org?.id) {
        const res = await PromptColumnsService.getPromptColumns(org?.id || '');
        return res;
      }
      return [];
    },
  });

  async function handleSave() {
    const updatableNode = nodes.find((n: any) => n.id === selectedNode.id);

    if (updatableNode) {
      try {
        await updateNode.mutateAsync({
          req: { data: updatableNode.data as DataFlaggerNodeData },
          nodeId: updatableNode.id,
        });

        // Show success toast notification
        toast.success('Data Flagger conditions saved successfully.', {
          description: 'Your changes have been saved.',
          duration: 5000,
        });
      } catch (error) {
        console.error(error);

        // Show error toast notification
        toast.error('Failed to save node.', {
          description: 'An error occurred while saving your changes.',
          duration: 5000,
        });
      }
    }
  }
  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  function isDataFlaggerNodeData(data: any): data is DataFlaggerNodeData {
    return data.type === 'DATA_FLAGGER';
  }

  useEffect(() => {
    if (promptColumns?.isSuccess) {
      // get the analyzer node from the nodes
      const dataFlaggerNode = nodes.find(
        (node: any) => node.type === 'DATA_FLAGGER',
      );
      // get the analyzer node from the nodes
      const analyzerNode = nodes.find((node: any) => node.type === 'ANALYZER');

      if (analyzerNode) {
        // setSelectedAgents([]);
        // first get the ids of all the agents
        const agentIds = Object.keys(analyzerNode.data?.agents || {});
        const formattedAgents: any = [];
        promptColumns?.data?.forEach((prompt: any) => {
          if (agentIds.includes(prompt.id)) {
            // don't insert duplicate records
            if (!selectedAgents.find((agent: any) => agent.id === prompt.id)) {
              formattedAgents.push(prompt);
            }
            setSelectedAgents(formattedAgents);
          }
        });
      }

      if (isDataFlaggerNodeData(selectedNode.data)) {
        if (
          selectedNode.data.conditions &&
          selectedNode.data.conditions.length > 0
        ) {
          setAgentConditions(selectedNode.data.conditions);
          setNotifyIfAnyDataIsFlagged(
            selectedNode.data.notifyIfAnyDataIsFlagged,
          );
          setFlaggedDataNotificationReceipients(
            selectedNode.data.flaggedDataNotificationReceipients,
          );
        }
      }
    }
  }, [selectedNode]);

  return (
    <div className="w-full h-full">
      <p className=" text-sm font-semibold [&:not(:first-child)]:mt-6">
        Flagging conditions
      </p>
      <div className="mb-4 flex items-center">
        <Info className="mr-1 h-3 w-3 text-muted-foreground" />
        <p className="tracking-light text-xs text-muted-foreground">
          Flag data based on pre defined conditions.
        </p>
      </div>

      {promptColumns.isLoading && <div>Loading...</div>}

      {agentConditions?.length === 0 && (
        <div className="flex items-center justify-start">
          <Button
            variant="outline"
            size="sm"
            className="h-6"
            disabled={updateNode.isPending}
            onClick={() => {
              addNewCondition({
                agentId: '',
                operator: 'eq',
                value: '',
              });
            }}
          >
            <Plus className="h-3 w-3" />
            Add new condition
          </Button>
        </div>
      )}

      {agentConditions &&
        agentConditions.map((con, c) => (
          <div className="mb-2 flex items-center justify-start" key={c}>
            {/* <pre>{JSON.stringify(con)}</pre> */}
            <Select
              onValueChange={value => {
                if (!readOnly) {  // ✅ Prevent updates in read-only mode
                  updateAgentCondition({
                    agentId: value,
                    operator: con?.operator as ConditionOperator,
                    value: con?.value,
                    id: con?.id,
                  });
                }
              }}
              value={con?.agentId}
              disabled={readOnly} // ✅ Disable select in read-only mode
            >

              <SelectTrigger className="mr-1 h-6 w-[200px] text-xs">
                <SelectValue placeholder="Agent" className="w-[180px]" />
              </SelectTrigger>
              <SelectContent className="w-[180px]">
                <SelectGroup>
                  <SelectLabel className="text-xs">Agents</SelectLabel>
                  {selectedAgents?.map((agent: any, a) => (
                    <SelectItem value={agent?.id} className="text-xs" key={a}>
                      {agent?.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Select
              onValueChange={value => {
                if (!readOnly) {
                  // update the data ingestion node configuration
                  updateAgentCondition({
                    agentId: con?.agentId,
                    operator: value as ConditionOperator,
                    value: con?.value,
                    id: con?.id,
                  });
                }
              }}
              value={con?.operator} // @NOTE: Need to fix the types later on
              disabled={readOnly}
            >
              <SelectTrigger className="mr-1 h-6 w-[100px] text-xs">
                <SelectValue placeholder="Operator" />
              </SelectTrigger>
              <SelectContent className="w-[60px]">
                <SelectGroup>
                  <SelectItem value="eq" className="text-xs">
                    Equals to
                  </SelectItem>
                  <SelectItem value="contains" className="text-xs">
                    Contains
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <Input
              type="text"
              placeholder="Value"
              className="mr-1 h-6 text-xs"
              value={con?.value}
              disabled={readOnly} // ✅ Disable input field
              onChange={e => {
                if (!readOnly) { // ✅ Prevent updates in read-only mode
                  updateAgentCondition({
                    agentId: con?.agentId,
                    operator: con?.operator,
                    value: e.target.value,
                    id: con?.id,
                  });
                }
              }}
            />


            <Button
              variant="outline"
              size="icon"
              className="mr-1 h-6"
              disabled={readOnly} // ✅ Disable button
              onClick={() => {
                if (!readOnly) { // ✅ Prevent action
                  addNewCondition({
                    agentId: '',
                    operator: 'eq',
                    value: '',
                  });
                }
              }}
            >
              <Plus className="h-3 w-6" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className="h-6"
              disabled={readOnly} // ✅ Disable button
              onClick={() => {
                if (!readOnly) { // ✅ Prevent action
                  removeAgentCondition(con.id);
                }
              }}
            >
              <Trash2 className="h-3 w-6" />
            </Button>

          </div>
        ))}

      {agentConditions && agentConditions.length > 0 && (
        <div className="mt-6 flex items-center space-x-2">
          <Checkbox
            id="notifyIfAnyDataIsFlagged"
            checked={notifyIfAnyDataIsFlagged}
            disabled={readOnly} // ✅ Disable in read-only mode
            onCheckedChange={(check: boolean) => {
              if (!readOnly) { // ✅ Prevent state updates
                setNotifyIfAnyDataIsFlagged(check);
                if (!check) {
                  setFlaggedDataNotificationReceipients('');
                }
                updateAgentConditionsOnNode(null, check, '');
              }
            }}
          />

          <label
            htmlFor="notifyIfAnyDataIsFlagged"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Send a notification if any data is flagged.
          </label>
        </div>
      )}

      {notifyIfAnyDataIsFlagged && (
        <div className="mt-4 flex flex-col">
          <div className="grid w-full  items-center gap-1.5">
            <Label htmlFor="flaggedDataNotificationReceipients">
              Receipients
            </Label>
            <p className="text-xs text-muted-foreground">
              Enter comma seperated values of email addresses where the email
              notification should be sent.
            </p>
            <Input
              type="text"
              id="flaggedDataNotificationReceipients"
              value={flaggedDataNotificationReceipients}
              disabled={readOnly} // ✅ Disable input field
              onChange={e => {
                if (!readOnly) { // ✅ Prevent updates
                  setFlaggedDataNotificationReceipients(e.target.value);
                  updateAgentConditionsOnNode(
                    null,
                    notifyIfAnyDataIsFlagged,
                    e.target.value
                  );
                }
              }}
              placeholder="ex: hello@tennisfinance.com, abc.xyz.com"
              className="mt-1 w-[100%]"
            />

          </div>
        </div>
      )}

      <div className="mt-2 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
